if (!customElements.get('channelsight-retailers')) {
	class ChannelsightRetailers extends HTMLElement {
		static observedAttributes = ['data-barcode'];
		constructor() {
			super();
			this.connected = false;
		}

		connectedCallback() {
			this.connected = true;
			this.initialize();
		}

		attributeChangedCallback(name, oldValue, newValue) {
			if (!this.connected) return;
			if (oldValue !== newValue) this.initialize();
		}

		initialize() {
			this.retailerList = this.querySelector('.retailers-list');
			if (!this.retailerList) {
				this.classList.add('hidden');
				return;
			}

			this.barcode = this.getAttribute('data-barcode');
			if (!this.barcode) {
				this.classList.add('hidden');
				return;
			}

			this.disableOutofStock = true;
			this.availabilityTypes = {
				inStock: 'InStock'
			};

			this.sortOptions = {
				price: 'price',
				title: 'title',
				pinned: 'pinned'
			};

			this.spinner = this.querySelector('.retailers-spinner');

			this.sortBy = this.sortOptions[this.getAttribute('data-sort')];

			this.enablePriceThresholds =
				this.getAttribute('data-thresholds') == 'true' ? true : false;
			this.globalPriceThreshold = this.getAttribute('data-global-threshold')
				? parseInt(this.getAttribute('data-global-threshold'))
				: 0;
			// These key names match to `RetailerName` returned by the API
			this.priceThresholds = {
				HomeDepot: this.getAttribute('data-home-depot-threshold')
					? parseInt(this.getAttribute('data-home-depot-threshold'), 10)
					: 0,
				AmazonUS: this.getAttribute('data-amazon-threshold')
					? parseInt(this.getAttribute('data-amazon-threshold'), 10)
					: 0,
				AshleyFurnitureUS: this.getAttribute('data-ashley-threshold')
					? parseInt(this.getAttribute('data-ashley-threshold'), 10)
					: 0
			};

			this.retailerProducts = [];

			this.fetchRetailers();
		}

		getCookie(name) {
			const regex = new RegExp(`(^| )${name}=([^;]+)`);
			const match = document.cookie.match(regex);
			if (match) {
				return match[2];
			}
		}

		fetchRetailers() {
			if (this.submitting) {
				this.submitting = false;
				this.controller.abort();
			}
			this.controller = new AbortController();
			this.signal = this.controller.signal;
			this.submitting = true;
			this.showSpinner();

			const medium = this.getCookie('utm_medium');
			const content = this.getCookie('utm_content');
			const source = this.getCookie('utm_source');
			const campaign = this.getCookie('utm_campaign');
			const gclid = this.getCookie('gclid');

			let endpoint = `https://api.channelsight.com/api/2.14/feed?key=c4d11394-8ab1-4b18-9f03-f9abf4a51dbf&assetId=5589&pid=${this.barcode}`;
			if (medium) endpoint += `&csi_utm_medium=${medium}`;
			if (content) endpoint += `&csi_utm_content=${content}`;
			if (source) endpoint += `&csi_utm_source=${source}`;
			if (campaign) endpoint += `&csi_utm_campaign=${campaign}`;
			if (gclid) endpoint += `&csi_subtag=${gclid}`;
			fetch(endpoint, {
				method: 'get',
				signal: this.signal
			})
				.then(function (response) {
					if (!response.ok) {
						return Promise.reject(result);
					}
					return response.json();
				})
				.then((response) => {
					if (
						!response.length ||
						!response[0].RetailerProducts ||
						!response[0].RetailerProducts.length
					) {
						this.classList.add('hidden');
						return;
					}
					this.retailerProducts = response[0].RetailerProducts;
					this.filter();
					if (!this.retailerProducts.length) {
						this.classList.add('hidden');
						return;
					}
					this.generate();
				})
				.catch((err) => {
					console.error(err);
					this.classList.add('hidden');
				})
				.finally(() => {
					this.submitting = false;
					this.hideSpinner();
				});
		}

		filter() {
			// Filter out $0 priced products returned (erroneously?) by the API
			const pricedProducts = [];
			this.retailerProducts.forEach((product) => {
				if (product.Price !== 0) pricedProducts.push(product);
			});
			this.retailerProducts = pricedProducts;

			// Hide out of stock retailers
			if (this.disableOutofStock) {
				this.retailerProducts = this.retailerProducts.filter(
					(retailer) => retailer.Availability === this.availabilityTypes.inStock
				);
			}

			// Filter by price thresholds
			if (this.enablePriceThresholds && this.retailerProducts.length > 1) {
				const lowestPriceRetailer = this.retailerProducts.reduce(
					(a, b) => (a.Price < b.Price ? a : b),
					{}
				);
				const lowestPrice = lowestPriceRetailer
					? parseInt(lowestPriceRetailer.Price, 10)
					: 0;
				const filteredProducts = [];
				this.retailerProducts.forEach((retailer) => {
					const retailerThreshold = this.priceThresholds[retailer.RetailerName];
					const retailerPrice = parseInt(retailer.Price, 10);
					// Thresholds
					// Check if any filtering is applicable
					if (
						(retailerThreshold === 0 && this.globalPriceThreshold === 0) ||
						lowestPrice === 0 ||
						retailerPrice === lowestPrice
					) {
						filteredProducts.push(retailer);
						return;
					}
					// Compare to global threshold for retailers that do not have a unique threshold set
					if (
						this.globalPriceThreshold > 0 &&
						retailerThreshold === 0 &&
						retailerPrice - lowestPrice < this.globalPriceThreshold
					) {
						filteredProducts.push(retailer);
						return;
					}
					if (retailerThreshold > 0 && retailerPrice - lowestPrice < retailerThreshold) {
						filteredProducts.push(retailer);
					}
				});
				this.retailerProducts = filteredProducts;
			}

			// Sort retailer output list
			if (this.sortBy && this.retailerProducts.length > 1) {
				this.retailerProducts.sort((a, b) => {
					if (this.sortBy === this.sortOptions.price) {
						return a.Price - b.Price;
					} else if (this.sortBy === this.sortOptions.title) {
						const firstRetailer = Array.from(a.FriendlyName)[0];
						const secondRetailer = Array.from(b.FriendlyName)[0];
						return firstRetailer.localeCompare(secondRetailer);
					}
					// Fallback
					else {
						return true;
					}
				});
			}
		}

		generate() {
			const container = document.createElement('ul');
			this.retailerProducts.forEach((retailer) => {
				const entry = document.createElement('li');
				const logoWrapper = document.createElement('div');
				const logo = document.createElement('img');
				logo.src = retailer.RetailerLogoUrl;
				logo.setAttribute('width', '70px');
				logo.setAttribute('height', '70px');
				logo.alt = retailer.RetailerName;
				logoWrapper.appendChild(logo);
				entry.appendChild(logoWrapper);
				const price = document.createElement('p');
				price.textContent = retailer.RetailerCurrencySymbol + Math.ceil(retailer.Price);
				entry.appendChild(price);
				const button = document.createElement('a');
				button.href = retailer.ClickThruUrl;
				button.target = '_blank';
				button.textContent = window.variantStrings.buyNow;
				button.classList.add(retailer.RetailerName);
				entry.appendChild(button);
				container.appendChild(entry);
			});
			this.retailerList.textContent = '';
			this.retailerList.appendChild(container);
			this.classList.remove('hidden');
			this.hideSpinner();
			setTimeout(() => {
				this.style.setProperty('--previous-height', `${this.scrollHeight}px`);
			});
		}

		hideSpinner() {
			this.spinner?.classList.add('hidden');
		}

		showSpinner() {
			this.spinner?.classList.remove('hidden');
		}
	}

	customElements.define('channelsight-retailers', ChannelsightRetailers);
}

if (!customElements.get('sofa-arrangements')) {
	class SofaArrangements extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.arrangements = this.querySelectorAll('.arrangement__tile');
			this.label = this.querySelector('[data-arrangement]');
			this.defaultVariantId = this.getAttribute('data-default-variant') || null;
			this.handleOnClick = this.onClick.bind(this);
			this.arrangements.forEach((arrangement) => {
				arrangement.addEventListener('click', this.onClick);
			});
			this.updateActive();
		}

		disconnectedCallback() {
			this.arrangements.forEach((arrangement) => {
				arrangement.removeEventListener('click', this.onClick);
			});
		}

		onClick(event) {
			event.preventDefault();
			if (event.target.classList.contains('arrangement__tile--active')) return;
			const target = event.target.href;
			const params = target.split('?');
			if (!params.length) {
				window.location.href = target;
				return;
			}

			const searchParams = new URLSearchParams(`?${params[1]}`);
			const variant = searchParams.get('variant');
			if (!variant) {
				window.location.href = target;
				return;
			}

			const variantOptions = this.closest('product-info')?.querySelector('variant-selects');
			if (!variantOptions) {
				window.location.href = target;
				return;
			}

			const variantId = parseInt(variant, 10);
			const newVariant = variantOptions.getVariantData().find((variant) => {
				return variant.id === variantId;
			});

			if (!newVariant) {
				window.location.href = target;
				return;
			}

			const fieldsets = Array.from(variantOptions.querySelectorAll('fieldset'));
			const requiredOptions = newVariant.options.length;

			if (fieldsets.length !== requiredOptions) {
				window.location.href = target;
				return;
			}

			let foundOptions = 0;

			fieldsets.forEach((fieldset, index) => {
				const option = fieldset.querySelector(`[value='${newVariant.options[index]}']`);
				if (option) {
					option.checked = true;
					foundOptions++;
				}
			});

			if (foundOptions !== requiredOptions) {
				window.location.href = target;
				return;
			}

			variantOptions.onVariantChange(event);
		}

		updateActive() {
			this.urlParams = new URLSearchParams(window.location.search);
			this.variantId = this.urlParams.get('variant');
			if (!this.variantId) {
				const variantOptions = this.closest('.product__info-container')?.querySelector(
					'variant-radios'
				);
				this.variantId = variantOptions?.currentVariant?.id;
			}
			if (!this.variantId) this.variantId = this.defaultVariantId;
			if (!this.variantId) return;
			this.currentLocation = window.location.pathname + `?variant=${this.variantId}`;
			this.activeIcon = null;
			this.arrangements.forEach((arrangement) => {
				const productLinks = arrangement.getAttribute('data-variants');
				const active = productLinks.includes(this.currentLocation);
				arrangement.classList.toggle('arrangement__tile--active', active);
				if (active) {
					this.activeIcon = arrangement;
				}
			});
			// Sofa fallback
			if (!this.activeIcon) {
				this.arrangements.forEach((arrangement) => {
					const productLinks = arrangement.getAttribute('data-variants');
					if (productLinks.includes(window.location.pathname)) {
						this.activeIcon = arrangement;
						arrangement.classList.add('arrangement__tile--active');
					}
				});
			}
			if (this.activeIcon) {
				this.label.textContent = this.activeIcon.getAttribute('data-label');
			}
		}
	}

	customElements.define('sofa-arrangements', SofaArrangements);
}

if (!customElements.get('product-delivery')) {
	class ProductDelivery extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.offsets = {
				startDate: {
					smallParcel: 2,
					LTL: 3
				},
				endDate: {
					smallParcel: 3,
					LTL: 20
				}
			};
			this.smallParcel = this.getAttribute('data-small-parcel') == 'true' ? true : false;
			this.LTL = this.getAttribute('data-ltl') == 'true' ? true : false;
			this.message = this.querySelector('[data-message]');
			if (!this.smallParcel && !this.LTL) {
				this.classList.add('hidden');
				this.parentElement.classList.add('hidden');
				return;
			}
			if (!this.message) {
				this.classList.add('hidden');
				this.parentElement.classList.add('hidden');
				return;
			}
			this.generateMessage();
		}

		generateMessage() {
			const startDate = new Date();
			const endDate = new Date();
			const startOffset = this.smallParcel
				? this.offsets.startDate.smallParcel
				: this.offsets.startDate.LTL;
			const endOffset = this.smallParcel
				? this.offsets.endDate.smallParcel
				: this.offsets.endDate.LTL;
			startDate.setDate(startDate.getDate() + startOffset);
			endDate.setDate(startDate.getDate() + endOffset);
			const deliveryMessage = `Free delivery by ${startDate.toLocaleString('default', {
				month: 'short'
			})} ${startDate.getDate()} - ${endDate.toLocaleString('default', {
				month: 'short'
			})} ${endDate.getDate()}`;
			this.message.textContent = deliveryMessage;
			this.classList.remove('hidden');
			this.parentElement.classList.remove('hidden');
		}
	}

	customElements.define('product-delivery', ProductDelivery);
}

if (!customElements.get('product-stock')) {
	class ProductStock extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.backInStockDate = this.getAttribute('data-back-in-stock-date');
			this.available = this.getAttribute('data-available') == 'true' ? true : false;
			this.quantity = this.getAttribute('data-quantity');
			this.message = this.querySelector('[data-message]');

			if (!this.message) {
				this.classList.add('hidden');
				this.parentElement.classList.add('hidden');
				return;
			}

			this.generateMessage();
		}

		generateMessage() {
			this.classList.remove('hidden');
			if (this.quantity && this.available) {
				const messageTemplate = window.variantStrings.inventoryStockCount;
				const newMessage = messageTemplate.replace('{{ quantity }}', this.quantity);
				this.message.textContent = newMessage;
				return;
			}
			if (!this.backInStockDate) {
				this.message.textContent = window.variantStrings.stockComingSoon;
				this.classList.remove('hidden');
				this.parentElement.classList.remove('hidden');
				return;
			}
		}
	}

	customElements.define('product-stock', ProductStock);
}

if (!customElements.get('pdp-modal-opener') && !customElements.get('pdp-swatch-opener')) {
	class PDPModalOpener extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.handleClick = this.onClick.bind(this);
			this.target = this.getAttribute('data-target');
			this.modal = this.target ? document.getElementById(this.target) : null;
			if (!this.modal) {
				this.classList.add('hidden');
				return;
			}
			this.addEventListener('click', this.handleClick);
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.handleClick);
		}

		onClick() {
			this.modal.show(this);
		}
	}
	customElements.define('pdp-modal-opener', PDPModalOpener);

	class PDPSwatchOpener extends PDPModalOpener {
		constructor() {
			super();
		}

		connectedCallback() {
			this.handleClick = this.onClick.bind(this);
			this.target = this.getAttribute('data-target');
			this.modal = this.target ? document.getElementById(this.target) : null;
			this.enabled = this.getAttribute('data-free-swatches') == 'true' ? true : false;
			this.swatchCollection = this.getAttribute('data-swatch-collection');
			if (!this.modal || !this.enabled || !this.swatchCollection) {
				this.classList.add('hidden');
				return;
			}
			this.addEventListener('click', this.handleClick);
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.handleClick);
		}

		onClick() {
			this.classList.add('loading');
			this.modal.generateSwatches(this);
		}
	}
	customElements.define('pdp-swatch-opener', PDPSwatchOpener);
}

if (!customElements.get('pdp-modal')) {
	class PDPModal extends HTMLElement {
		constructor() {
			super();
			document.body.appendChild(this);
		}

		connectedCallback() {
			this.productContainer = this.querySelector('[data-products]');
			this.closeButtons = this.querySelectorAll('[data-close-modal]');
			this.modalContainer = this.querySelector('[data-container]');
			this.visible = this.classList.contains('modal--active') ? true : false;
			this.setAttribute('role', 'dialog');
			const label = this.querySelector('.modal-heading')?.textContent;
			this.setAttribute('aria-label', label || 'Information Popup');
			if (!this.visible) {
				this.setAttribute('tabindex', -1);
			}
		}

		disconnectedCallback() {
			this.unbind();
		}

		bind() {
			this.handleClose = this.hide.bind(this);
			this.handleOutsideClick = this.onOutsideClick.bind(this);
			this.closeButtons.forEach((button) => {
				button.addEventListener('click', this.handleClose);
			});
			this.addEventListener('click', this.handleOutsideClick);
		}

		unbind() {
			this.closeButtons.forEach((button) => {
				button.removeEventListener('click', this.handleClose);
			});
			this.removeEventListener('click', this.handleOutsideClick);
		}

		show(opener) {
			if (this.visible) return;
			this.opener = opener;
			document.body.classList.add('overflow-hidden');
			this.classList.add('modal--active');
			const button = this.querySelector('button');
			trapFocus(this, button);
			this.removeAttribute('tabindex');
			this.visible = true;
			this.bind();
		}

		hide() {
			this.classList.remove('modal--active');
			document.body.classList.remove('overflow-hidden');
			this.visible = false;
			removeTrapFocus();
			this.unbind();
			this.setAttribute('tabindex', -1);
		}

		onOutsideClick(event) {
			if (!this.visible) return;
			if (
				event.target !== this.modalContainer &&
				!this.modalContainer.contains(event.target) &&
				event.target !== this.opener &&
				!this.opener?.contains(event.target)
			) {
				this.hide();
			}
		}
	}
	customElements.define('pdp-modal', PDPModal);
}

if (!customElements.get('pdp-swatch-modal')) {
	class PDPSwatchModal extends HTMLElement {
		constructor() {
			super();
			this.connected = false;
			document.body.appendChild(this);
		}

		connectedCallback() {
			this.productContainer = this.querySelector('[data-products]');
			this.closeButtons = this.querySelectorAll('[data-close-modal]');
			this.handleClose = this.hide.bind(this);
			this.handleOutsideClick = this.onOutsideClick.bind(this);
			this.modalContainer = this.querySelector('[data-container]');
			this.visible = this.classList.contains('modal--active') ? true : false;
			this.swatches = [];
			if (!this.visible) {
				this.setAttribute('tabindex', -1);
			}
		}

		disconnectedCallback() {
			this.unbind();
		}

		bind() {
			this.closeButtons.forEach((button) => {
				button.addEventListener('click', this.handleClose);
			});
			this.addEventListener('click', this.handleOutsideClick);
		}

		unbind() {
			this.closeButtons.forEach((button) => {
				button.removeEventListener('click', this.handleClose);
			});
			this.removeEventListener('click', this.handleOutsideClick);
		}

		show() {
			this.visible = true;
			this.classList.add('modal--active');
			document.body.classList.add('overflow-hidden');
			const button = this.querySelector('button');
			this.opener.classList.remove('loading');
			trapFocus(this, button);
			this.removeAttribute('tabindex');
			this.bind();
		}

		hide() {
			this.classList.remove('modal--active');
			document.body.classList.remove('overflow-hidden');
			removeTrapFocus();
			this.unbind();
			this.setAttribute('tabindex', -1);
			this.visible = false;
		}

		onOutsideClick(event) {
			if (!this.visible) return;
			if (
				event.target !== this.modalContainer &&
				!this.modalContainer.contains(event.target) &&
				event.target !== this.opener &&
				!this.opener?.contains(event.target)
			) {
				this.hide();
			}
		}

		async generateSwatches(opener) {
			if (!opener || !opener.swatchCollection) return;
			if (this.swatchCollection === opener.swatchCollection) {
				this.show();
				return;
			}
			this.opener = opener;
			this.productContainer.textContent = '';
			this.swatches = [];
			this.swatchCollection = opener.swatchCollection;
			this.productData = await this.fetchProducts();
			if (!this.productData) return;
			this.productData.forEach((product) => {
				this.swatches.push(new FreeSwatchProduct(product));
				this.productContainer.appendChild(this.swatches[this.swatches.length - 1].html);
			});
			this.show();
		}

		async fetchProducts() {
			const graphQuery = `
				{nodes(ids: ["${this.swatchCollection}"]){
					...on Collection {
						id
						products(first: 150) {
							edges {
								node {
									id
									title
									description
									variants(first: 1) {
										edges {
											node {
												id
											}
										}
									}
									images(first: 1) {
										edges {
											node {
												id
												url
												width
												height
											}
										}
									}
								}
							}
						}
					}
				}
			}`;

			const response = await fetch('/api/2023-04/graphql.json', {
				method: 'POST',
				headers: {
					'X-Shopify-Storefront-Access-Token': p80.storefrontToken,
					'Content-Type': 'application/graphql'
				},
				body: graphQuery
			});

			try {
				if (!response.ok) {
					const json = await response.json();
					throw new Error(json);
				}
				const json = await response.json();
				if (!json.data || !json.data.nodes || !json.data.nodes.length) {
					throw new Error('No data returned.');
				}
				return json.data.nodes[0].products.edges;
			} catch (error) {
				console.error(error);
				return null;
			}
		}
	}
	customElements.define('pdp-swatch-modal', PDPSwatchModal);
}

if (!window.FreeSwatchProduct) {
	class FreeSwatchProduct {
		constructor(data) {
			this.data = data ? data.node : null;
			if (!this.data) return;
			this.loading = false;
			this.added = false;
			this.generateHTML();
			this.bind();
		}

		generateHTML() {
			this.html = document.createElement('li');
			if (
				this.data.images &&
				this.data.images.edges &&
				this.data.images.edges[0] &&
				this.data.images.edges[0].node
			) {
				const swatchImage = document.createElement('img');
				swatchImage.setAttribute('src', this.data.images.edges[0].node.url + '&width=545');
				swatchImage.setAttribute('width', 545);
				swatchImage.setAttribute('height', 545);
				swatchImage.setAttribute('loading', 'lazy');
				swatchImage.setAttribute('alt', this.data.title);
				this.html.appendChild(swatchImage);
			}
			const swatchTitle = document.createElement('h3');
			swatchTitle.textContent = this.data.title;
			this.html.appendChild(swatchTitle);

			const swatchDescription = document.createElement('p');
			swatchDescription.textContent = this.data.description;
			this.html.appendChild(swatchDescription);

			const errorText = document.createElement('p');
			errorText.classList.add('error', 'hidden', 'small', 'brick');
			this.html.appendChild(errorText);
			this.errorText = this.html.querySelector('.error');

			const addToCartButton = document.createElement('button');
			const textWrapper = document.createElement('span');
			textWrapper.textContent = window.variantStrings.add;
			textWrapper.classList.add('add');
			addToCartButton.appendChild(textWrapper);
			const spinnerWrapper = document.createElement('span');
			spinnerWrapper.innerHTML = `
	  <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
					<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
				  </svg>
		`;
			spinnerWrapper.classList.add('spinner');
			addToCartButton.appendChild(spinnerWrapper);
			const checkWrapper = document.createElement('span');
			checkWrapper.innerHTML = `
	  <svg class="icon icon-checkmark color-foreground-text" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none">
		<path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"></path>
	  </svg>
	  `;
			checkWrapper.classList.add('check');
			addToCartButton.appendChild(checkWrapper);
			addToCartButton.setAttribute('type', 'button');
			addToCartButton.classList.add(
				'custom-button',
				'custom-button--small',
				'custom-button--white'
			);
			this.html.appendChild(addToCartButton);
			this.button = this.html.querySelector('button');
		}

		bind() {
			const swatch = this;
			if (!this.button) return;
			this.button.addEventListener('click', function () {
				swatch.addToCart();
			});
		}

		addToCart() {
			if (this.loading || this.added || !this.button) return;
			this.loading = !this.loading;
			this.button.classList.add('loading');
			this.errorText.classList.add('hidden');

			const variantData = {
				id: parseInt(
					this.data.variants.edges[0].node.id.split('/')[
						this.data.variants.edges[0].node.id.split('/').length - 1
					],
					10
				),
				quantity: 1
			};

			const formData = {
				items: [variantData]
			};

			fetch(window.Shopify.routes.root + 'cart/add.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			})
				.then((response) => {
					return response.json();
				})
				.then((response) => {
					if (response.status) {
						throw new Error(response.description);
						return;
					}
					this.button.querySelector('span').textContent = window.variantStrings.added;
					this.added = true;
					this.button.classList.add('custom-button--disabled');
					if (p80 && p80.ajaxCart) {
						p80.cart.fetchCart();
					}
				})
				.catch((error) => {
					console.error('Error:', error);
					const message = error.message.replace('Error: ', '');
					this.errorText.textContent = message;
					this.errorText.classList.remove('hidden');
				})
				.finally(() => {
					this.loading = !this.loading;
					this.button.classList.remove('loading');
				});
		}
	}
	window.FreeSwatchProduct = FreeSwatchProduct;
}

if (!customElements.get('product-tabs')) {
	class ProductTabs extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.root = this.closest('.main-product-container');
			this.header = this.root?.querySelector('.product__header-sticky');
			this.stickyContainer = this.querySelector('.product__tabs-sticky');

			this.tabButtons = [...this.querySelectorAll('.product-tabs__header [data-target]')];
			this.tabs = [...this.querySelectorAll('[data-tab-id]')];
			this.tabs.unshift(this.root?.querySelector('.product'));

			if (!this.tabButtons.length || !this.tabs.length) {
				this.classList.add('hidden');
				return;
			}

			this.activeTab = this.tabButtons[0];
			this.prevActiveTab = this.activeTab;
			this.direction = 'up';
			this.prevPosition = 0;
			this.rootMargin = `0px`;

			const tasks = [this.bind.bind(this), this.setObservers.bind(this)];
			this.yieldTasks(tasks);
		}

		disconnectedCallback() {
			this.unbind();
		}

		yieldToMain() {
			return new Promise((resolve) => {
				setTimeout(resolve, 0);
			});
		}

		async yieldTasks(tasks) {
			while (tasks.length > 0) {
				const task = tasks.shift();
				task();
				await this.yieldToMain();
			}
		}

		bind() {
			this.intersectionHandler = this.onIntersect.bind(this);
			this.stickyBarUnsubscriber = subscribe(PUB_SUB_EVENTS.stickyBarUpdate, (event) => {
				this.rebound = true;
				const tasks = [this.setObservers.bind(this)];
				this.yieldTasks(tasks);
			});
			this.handleScroll = this.scrollToTab.bind(this);
			this.tabButtons.forEach((button) => {
				// Hide buttons which do not have applicable content returned
				const target = button.getAttribute('data-target');
				const targetElement = this.querySelector(`[data-tab-id='${target}']`);
				if (targetElement || target === 'overview') {
					button.addEventListener('click', this.handleScroll);
				} else {
					button.classList.add('hidden');
				}
			});
		}

		unbind() {
			if (this.stickyBarUnsubscriber) this.stickyBarUnsubscriber();
			this.tabButtons.forEach((button) => {
				button.removeEventListener('click', this.handleScroll);
			});
			this.intersectionObserver?.disconnect();
		}

		setObservers() {
			this.direction = 'up';
			this.prevPosition = window.scrollY;

			this.headerOffset = this.header.currentHeight + this.header.ignoreHeight;
			this.stickyOffset = this.stickyContainer.ignoreHeight;
			this.rootMargin = `${(this.headerOffset + this.stickyOffset) * -1}px 0px 0px 0px`;
			this.intersectionObserver?.disconnect();
			this.intersectionObserver = new IntersectionObserver(
				(entries) => this.intersectionHandler(entries),
				{threshold: 0, rootMargin: this.rootMargin}
			);
			this.tabs.forEach((tab) => {
				this.intersectionObserver.observe(tab);
			});
		}

		onIntersect(entries) {
			entries.forEach((entry) => {
				this.direction = window.scrollY > this.prevPosition ? 'down' : 'up';
				this.prevPosition = window.scrollY;
				if (!this.shouldUpdate(entry)) return;
				const target = this.getTargetSection(entry);
				this.setActive(target);
			});
			this.rebound = false;
		}

		shouldUpdate(entry) {
			if (this.direction === 'down' && !entry.isIntersecting) {
				return true;
			}
			if (this.direction === 'up' && entry.isIntersecting && !this.rebound) {
				return true;
			}
			return false;
		}

		getTargetSection(entry) {
			if (this.direction === 'up') return entry.target;
			const index = this.tabs.findIndex((tab) => tab === entry.target);
			if (index >= this.tabs.length - 1) {
				return this.tabs[this.tabs.length - 1];
			} else {
				return this.tabs[index + 1];
			}
		}

		setActive(tab) {
			if (!tab) return;
			if (this.activeTab) this.prevActiveTab = this.activeTab;
			const type = tab.getAttribute('data-tab-id') || 'overview';
			this.activeTab?.classList.remove('product-tabs__button--active');
			this.activeTab = null;
			this.tabButtons.forEach((button) => {
				const buttonType = button.getAttribute('data-target');
				if (buttonType === type) {
					this.activeTab = button;
					this.activeTab?.classList.add('product-tabs__button--active');
					this.updateActiveButton();
				}
			});
			if (!this.activeTab) {
				this.prevActiveTab.classList.add('product-tabs__button--active');
			} else if (this.activeTab && this.activeTab !== this.prevActiveTab) {
				this.prevActiveTab.classList.remove('product-tabs__button--active');
			}
		}

		updateActiveButton() {
			if (!this.activeTab) return;
			const container = this.activeTab.closest('ul');
			const containerBounding = container.getBoundingClientRect();
			const containerWidth = containerBounding.width;
			const containerOffset = container.scrollLeft;

			const buttonBounding = this.activeTab.getBoundingClientRect();
			const buttonWidth = buttonBounding.width;
			const buttonOffset = this.activeTab.offsetLeft;

			const lastVisible = containerWidth + containerOffset;

			const isVisible =
				buttonOffset + buttonWidth <= lastVisible && buttonOffset >= containerOffset;

			if (isVisible) {
				this.style.setProperty('--highlight-width', `${buttonWidth}px`);
				this.style.setProperty('--highlight-offset', `${buttonOffset - containerOffset}px`);
				return;
			}

			const scrollOffset =
				buttonBounding.right > containerBounding.right
					? containerOffset + buttonBounding.right - containerBounding.right
					: buttonOffset;

			this.style.setProperty('--highlight-width', `${buttonWidth}px`);
			this.style.setProperty('--highlight-offset', `${buttonOffset - scrollOffset}px`);

			container.scrollTo({
				top: 0,
				left: scrollOffset,
				behavior: 'smooth'
			});
		}

		scrollToTab(event) {
			if (!event) return;
			const target = event.target.getAttribute('data-target');
			if (target === 'overview') {
				window.scroll({
					top: 0,
					behavior: 'smooth'
				});
				return;
			}

			const targetElement = this.querySelector(`[data-tab-id='${target}']`);
			if (!targetElement) return;
			const bounding = targetElement.getBoundingClientRect();
			const offset = bounding.top + window.scrollY;
			let finalOffset;
			if (target === 'overview') {
				finalOffset = offset;
			} else {
				finalOffset = offset - this.headerOffset + 1;
			}
			window.scroll({
				top: finalOffset,
				behavior: 'smooth'
			});
		}
	}
	customElements.define('product-tabs', ProductTabs);
}
